import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarCircleOutlined,
  EyeFilled,
  HistoryOutlined,
  RedoOutlined,
  ShoppingCartOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  ASelect,
} from '../../components';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Modal,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
} from 'antd';
import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';
import { getSale, getSales } from '../../http';
import { ISale, ISaleDetails, ISaleItem } from '../../shared/interfaces';
import { currency } from '../../shared/utils/functions';
import { formatDate } from '../../shared/utils/format-date';
import { Screens } from '../../shared/enums/screens.enum';

const SALE_TYPE_OPTIONS = [
  { label: 'Todas as vendas', value: '' },
  { label: 'Venda rápida', value: 'quick-sale' },
  { label: 'Venda vinculada ao tratamento', value: 'retail-sale' },
];

const getSaleTypeLabel = (type?: string) => {
  if (type === 'quick-sale') return 'Venda rápida';
  if (type === 'retail-sale') return 'Venda vinculada';
  return 'Venda';
};

const getSaleTypeColor = (type?: string) => {
  if (type === 'quick-sale') return 'blue';
  if (type === 'retail-sale') return 'gold';
  return 'default';
};

const getSaleStatusLabel = (status?: number) => {
  if (status === 5) return 'Cancelada';
  return 'Confirmada';
};

const getSaleStatusColor = (status?: number) => {
  if (status === 5) return 'red';
  return 'green';
};

const SalesPage: React.FC = () => {
  const navigate = useNavigate();
  const { screenFind } = useAuth();
  const { addToast } = useToast();
  const [search, setSearch] = useState('');
  const [saleType, setSaleType] = useState<string>('');
  const [sales, setSales] = useState<ISale[]>([]);
  const [selectedSale, setSelectedSale] = useState<ISaleDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [modalDetail, setModalDetail] = useState(false);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState<number>();

  const loadSales = useCallback(async () => {
    setLoading(true);
    setSales([]);

    try {
      const response = await getSales(
        limit,
        (page - 1) * limit,
        search,
        saleType || undefined,
      );

      setSales(response.data);
      setPages(response.count);
    } catch {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao carregar o acompanhamento das vendas.',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, limit, page, saleType, search]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  useEffect(() => {
    setPage(1);
  }, [search, saleType]);

  const openSaleDetail = useCallback(
    async (saleId: string) => {
      setModalDetail(true);
      setDetailLoading(true);

      try {
        const response = await getSale(saleId);
        setSelectedSale(response);
      } catch {
        setModalDetail(false);
        addToast({
          type: 'error',
          title: 'Erro',
          description: 'Não foi possível carregar os detalhes da venda.',
        });
      } finally {
        setDetailLoading(false);
      }
    },
    [addToast],
  );

  const pageTotal = useMemo(
    () => sales.reduce((sum, sale) => sum + Number(sale.total_amount || 0), 0),
    [sales],
  );

  const quickSalesCount = useMemo(
    () => sales.filter((sale) => sale.type === 'quick-sale').length,
    [sales],
  );

  const retailSalesCount = useMemo(
    () => sales.filter((sale) => sale.type === 'retail-sale').length,
    [sales],
  );

  return (
    <PageLayout>
      <PageIntro
        badge="Atendimento"
        icon={<HistoryOutlined />}
        title="Acompanhamento de vendas"
        description="Consulte o histórico das vendas de produtos, confira itens vendidos e visualize os lançamentos financeiros gerados em cada venda."
        actions={
          screenFind([Screens.quickSaleView]) ? (
            <Button
              type="primary"
              icon={<ShoppingCartOutlined />}
              onClick={() => navigate('/sale')}
            >
              Nova venda
            </Button>
          ) : undefined
        }
      />

      <MenuOptions
        showButtonBack
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => loadSales(),
          },
          ...(screenFind([Screens.quickSaleView])
            ? [
                {
                  label: 'Nova venda',
                  key: '2',
                  icon: <DollarCircleOutlined />,
                  onClick: () => navigate('/sale'),
                },
              ]
            : []),
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />

      <PagePanel>
        <Card>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={10} lg={8}>
              <strong>Tipo de venda</strong>
              <ASelect
                value={saleType}
                onChange={(value: string) => setSaleType(value || '')}
                options={SALE_TYPE_OPTIONS}
                style={{ width: '100%', marginTop: 8 }}
              />
            </Col>
            <Col xs={24} md={14} lg={16}>
              <span style={{ color: '#64748b', display: 'block' }}>
                Use a pesquisa para encontrar por cliente, observação ou número
                da venda.
              </span>
            </Col>
          </Row>
        </Card>
      </PagePanel>

      <PagePanel>
        <Row gutter={16}>
          <Col xs={24} sm={12} lg={8}>
            <Card>
              <Statistic
                title="Vendas nesta página"
                value={sales.length}
                prefix={<SnippetsOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card>
              <Statistic
                title="Valor listado"
                value={pageTotal}
                precision={2}
                formatter={(value) => currency(Number(value || 0))}
                prefix={<DollarCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card>
              <Statistic title="Rápidas" value={quickSalesCount} />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Card>
              <Statistic title="Vinculadas" value={retailSalesCount} />
            </Card>
          </Col>
        </Row>
      </PagePanel>

      <PagePanel>
        <ATable
          scroll={{ y: 510, x: 1100 }}
          columns={[
            {
              title: 'Venda',
              dataIndex: 'code',
              width: 90,
              render: (value: number) => `#${value}`,
            },
            {
              title: 'Data',
              dataIndex: 'date',
              width: 120,
              render: (value: string) => formatDate(value),
            },
            {
              title: 'Cliente',
              dataIndex: 'client_name',
              width: 240,
            },
            {
              title: 'Origem',
              dataIndex: 'type',
              width: 170,
              render: (value: string, record: ISale) => (
                <Space direction="vertical" size={4}>
                  <Tag color={getSaleTypeColor(value)}>
                    {getSaleTypeLabel(value)}
                  </Tag>
                  {record.treatment_code ? (
                    <span>Tratamento #{record.treatment_code}</span>
                  ) : null}
                </Space>
              ),
            },
            {
              title: 'Valor',
              dataIndex: 'total_amount',
              width: 120,
              render: (value: number) => (
                <strong>{currency(Number(value || 0))}</strong>
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 120,
              render: (value: number) => (
                <Tag color={getSaleStatusColor(value)}>
                  {getSaleStatusLabel(value)}
                </Tag>
              ),
            },
            {
              title: 'Observação',
              dataIndex: 'observation',
              width: 220,
              render: (value: string | null) => value || '-',
            },
            {
              title: 'Opções',
              width: 90,
              fixed: 'right' as const,
              render: (record: ISale) => (
                <DropdownOptions
                  items={[
                    {
                      label: 'Visualizar',
                      key: '1',
                      icon: <EyeFilled />,
                      onClick: () => openSaleDetail(record.id),
                    },
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={sales}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>

      <Modal
        title={
          selectedSale ? `Venda #${selectedSale.code}` : 'Detalhes da venda'
        }
        open={modalDetail}
        onCancel={() => {
          setModalDetail(false);
          setSelectedSale(null);
        }}
        footer={null}
        width={1080}
      >
        {selectedSale ? (
          <>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Cliente">
                {selectedSale.client_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Documento">
                {selectedSale.client_document || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Data">
                {formatDate(selectedSale.date)}
              </Descriptions.Item>
              <Descriptions.Item label="Origem">
                <Tag color={getSaleTypeColor(selectedSale.type)}>
                  {getSaleTypeLabel(selectedSale.type)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getSaleStatusColor(selectedSale.status)}>
                  {getSaleStatusLabel(selectedSale.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Valor total">
                <strong>
                  {currency(Number(selectedSale.total_amount || 0))}
                </strong>
              </Descriptions.Item>
              <Descriptions.Item label="Tratamento" span={2}>
                {selectedSale.treatment_code
                  ? `Tratamento #${selectedSale.treatment_code}`
                  : 'Não vinculado a tratamento'}
              </Descriptions.Item>
              <Descriptions.Item label="Observação" span={2}>
                {selectedSale.observation || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Itens vendidos</Divider>
            <Table
              rowKey="id"
              pagination={false}
              size="small"
              dataSource={selectedSale.items}
              columns={[
                {
                  title: 'Produto',
                  dataIndex: 'product_name_snapshot',
                  render: (_: string, item: ISaleItem) => {
                    const code =
                      item.product_code_snapshot || item.current_product_code;
                    const name =
                      item.product_name_snapshot || item.current_product_name;
                    const unity = item.product_unity_snapshot;

                    return [code ? `#${code}` : null, name, unity]
                      .filter(Boolean)
                      .join(' • ');
                  },
                },
                {
                  title: 'Quantidade',
                  dataIndex: 'quantity',
                  width: 110,
                },
                {
                  title: 'Valor UN.',
                  dataIndex: 'unit_price',
                  width: 130,
                  render: (value: number) => currency(Number(value || 0)),
                },
                {
                  title: 'Total',
                  dataIndex: 'total_price',
                  width: 130,
                  render: (value: number) => currency(Number(value || 0)),
                },
              ]}
            />

            <Divider orientation="left">Financeiro gerado</Divider>
            <Table
              rowKey="id"
              pagination={false}
              size="small"
              dataSource={selectedSale.financials}
              columns={[
                {
                  title: 'Lançamento',
                  dataIndex: 'code',
                  width: 100,
                  render: (value: number) => (value ? `#${value}` : '-'),
                },
                {
                  title: 'Vencimento',
                  dataIndex: 'due_date',
                  width: 130,
                  render: (value: string) => formatDate(value),
                },
                {
                  title: 'Forma',
                  dataIndex: 'payment_description',
                  render: (value: string) => value || '-',
                },
                {
                  title: 'Descrição',
                  dataIndex: 'description',
                },
                {
                  title: 'Valor',
                  dataIndex: 'value',
                  width: 130,
                  render: (value: number) => currency(Number(value || 0)),
                },
              ]}
            />
          </>
        ) : null}
      </Modal>

      <Modal open={detailLoading} footer={null} closable={false} width={320}>
        Carregando detalhes da venda...
      </Modal>
    </PageLayout>
  );
};

export default SalesPage;
